import { useCallback, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { navigate } from 'wouter/use-browser-location';
import { checkProductCodeExists } from '@/api/product-api';
import { offerCatalogRestore } from '@/api/catalog-recovery';
import { ProductFormValues } from '@/types/product-type';
import { ApiResponse } from '@/types/api-response';

export function useProductCodeValidation(form: UseFormReturn<ProductFormValues>) {
  const { watch, getValues, setError, clearErrors, trigger } = form;
  const code = watch('code');
  const checks = useRef(new Map<string, Promise<ApiResponse<boolean>>>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const validateCodeUniqueness = useCallback((value: string): Promise<boolean> => {
    const code = value.trim();
    if (!code) return trigger('code');
    let request = checks.current.get(code);
    if (!request) {
      request = checkProductCodeExists(code);
      checks.current.set(code, request);
    }
    const isCurrent = () => mounted.current && getValues('code').trim() === code;
    const check = (async () => {
      try {
        const response = await request;
        if (!isCurrent()) return false;
        if (response.success) {
          clearErrors('code');
          return trigger('code');
        }
        const restored = await offerCatalogRestore(response);
        if (!isCurrent()) return false;
        if (restored) {
          navigate(`/products/${restored.productId}/edit`);
          return false;
        }
        setError('code', { type: 'manual', message: response.message || 'Product code already exists' });
        return false;
      } catch (error) {
        checks.current.delete(code);
        if (isCurrent()) setError('code', { type: 'manual', message: error instanceof Error ? error.message : 'Could not validate product code' });
        return false;
      } finally {
        if (!isCurrent()) checks.current.delete(code);
      }
    })();
    return check;
  }, [clearErrors, getValues, setError, trigger]);

  useEffect(() => {
    clearErrors('code');
    if (!code?.trim()) return;
    const timer = setTimeout(() => { void validateCodeUniqueness(code); }, 600);
    return () => clearTimeout(timer);
  }, [code, clearErrors, validateCodeUniqueness]);

  return validateCodeUniqueness;
}
